import React, {useState} from "react";
import {useAppSelector} from "../../../../../redux/store";
import {Message, MessageSections} from "../../../../components/message/message";
import {StandardDialogFooter} from "../../../../components/dialogs/standard-dialog/standard-dialog";
import BarekamInput from "../../../../components/input";
import {BarekamIcon, IconVariants} from "../../../../components/icon/barekam-icon";
import {ChangePasswordContainer} from "../styled";
import allUsersService from "../../../../../services/all-users.service";
import {UserAddressUpdateMeta} from "../../../../../services/user-addresses.service";
import {initSuccessToast, mapValidationErrors} from "../personal-info.service";
import {AxiosError} from "axios";
import {DialogContent} from "@mui/material";
import {getMessage} from "../../../../shared/helper-functions";

export const PersonalInfoChangePassword = ({onClose}: PersonalInfoChangePasswordProps) => {
    const {currentUser} = useAppSelector(state => state.currentUserReducer);

    const [currentPassword, setCurrentPassword] = useState('');
    const [currentPasswordViewMode, setCurrentPasswordViewMode] = useState<string>('password');

    const [newPassword, setNewPassword] = useState('');
    const [newPasswordViewMode, setNewPasswordViewMode] = useState<string>('password');

    const [repeatedPassword, setRepeatedPassword] = useState('');
    const [repeatedPasswordViewMode, setRepeatedPasswordViewMode] = useState<string>('password');

    const [errors, setErrors] = useState<{[key: string]: string}>({});

    const {messageCache, languageId} = useAppSelector(state => state.messagesReducer);

    function handleSave() {
        const errors = validate();

        if (Object.keys(errors).length) {
            setErrors(errors);
        } else {
            changePassword()
        }
    }

    function validate() {
        const errors: {[key: string]: any} = {};

        if (!currentPassword) {
            errors.currentPassword = 'field_is_required';
        }
        if (!newPassword) {
            errors.newPassword = 'field_is_required';
        }
        if (!repeatedPassword) {
            errors.repeatedPassword = 'field_is_required';
        }
        if (currentPassword === newPassword) {
            errors.newPassword = 'passwords_are_same';
        }
        if (repeatedPassword !== newPassword) {
            errors.repeatedPassword = 'passwords_do_not_match';
        }

        return errors;
    }

    async function changePassword() {
        try {
            const data = {
                email: currentUser?.email,
                password: currentPassword,
                newPassword: newPassword
            }
            allUsersService.changePassword(currentUser?.userId, data)
                .then((response: UserAddressUpdateMeta | undefined) => {
                    if (response && response.acknowledged) {
                        const successMessage = getMessage('saved', MessageSections.USER, messageCache, languageId);
                        initSuccessToast(successMessage);

                        onClose(true);
                    }
                })
                .catch(e => {
                    if (e instanceof AxiosError) {
                        const errors = mapValidationErrors(e);
                        if (errors.password) {
                            errors.currentPassword = errors.password;
                            delete errors.password
                        }
                        setErrors(errors);
                    }
                })
        } catch (e) {
            if (e instanceof AxiosError) {
                const errors = mapValidationErrors(e);
                if (errors.password) {
                    errors.currentPassword = errors.password;
                    delete errors.password
                }
                setErrors(mapValidationErrors(e));
            }
        }
    }

    function toggleViewMode(type: 'current' | 'new' | 'repeated') {
        switch (type) {
            case "current":
                setCurrentPasswordViewMode(prevState => prevState === 'password' ? 'text' : 'password');
                break;
            case "new":
                setNewPasswordViewMode(prevState => prevState === 'password' ? 'text' : 'password');
                break;
            case "repeated":
                setRepeatedPasswordViewMode(prevState => prevState === 'password' ? 'text' : 'password');
                break;
        }
    }

    return (
        <>
            <DialogContent>
                <ChangePasswordContainer>
                    <BarekamInput key={'current-password'}
                                  customInputVariant={"standard"}
                                  placeholder={getMessage('password', MessageSections.USER, messageCache, languageId)}
                                  value={currentPassword}
                                  onChange={(value) => setCurrentPassword(value.target.value)}
                                  InputProps={{
                                      endAdornment: <BarekamIcon onClick={() => toggleViewMode('current')}
                                                                 isLoginPage
                                                                 size={{width: '1.2rem', height: '1.2rem'}}
                                                                 variant={currentPasswordViewMode === 'password'
                                                                     ? IconVariants.eyeHidden
                                                                     : IconVariants.eye}
                                      />
                                  }}
                                  type={currentPasswordViewMode}
                                  error={!!errors['currentPassword']}
                                  errorMessage={errors['currentPassword'] && getMessage(errors['currentPassword'], MessageSections.USER, messageCache, languageId)}
                                  width={'100%'}
                                  required/>

                    <BarekamInput key={'new-password'}
                                  customInputVariant={"standard"}
                                  placeholder={getMessage('new_password', MessageSections.USER, messageCache, languageId)}
                                  value={newPassword}
                                  onChange={(value) => setNewPassword(value.target.value)}
                                  InputProps={{
                                      endAdornment: <BarekamIcon onClick={() => toggleViewMode('new')}
                                                                 isLoginPage
                                                                 size={{width: '1.2rem', height: '1.2rem'}}
                                                                 variant={newPasswordViewMode === 'password'
                                                                     ? IconVariants.eyeHidden
                                                                     : IconVariants.eye}
                                      />
                                  }}
                                  type={newPasswordViewMode}
                                  error={!!errors['newPassword']}
                                  errorMessage={errors['newPassword'] && getMessage(errors['newPassword'], MessageSections.USER, messageCache, languageId)}
                                  width={'100%'}
                                  required/>

                    <BarekamInput key={'repeat-password'}
                                  customInputVariant={"standard"}
                                  placeholder={getMessage('repeat_password', MessageSections.USER, messageCache, languageId)}
                                  value={repeatedPassword}
                                  onChange={(value) => setRepeatedPassword(value.target.value)}
                                  InputProps={{
                                      endAdornment: <BarekamIcon onClick={() => toggleViewMode('repeated')}
                                                                 isLoginPage
                                                                 size={{width: '1.2rem', height: '1.2rem'}}
                                                                 variant={repeatedPasswordViewMode === 'password'
                                                                     ? IconVariants.eyeHidden
                                                                     : IconVariants.eye}
                                      />
                                  }}
                                  type={repeatedPasswordViewMode}
                                  error={!!errors['repeatedPassword']}
                                  errorMessage={errors['repeatedPassword'] && getMessage(errors['repeatedPassword'], MessageSections.USER, messageCache, languageId)}
                                  width={'100%'}
                                  required/>
                </ChangePasswordContainer>
            </DialogContent>

            <StandardDialogFooter
                saveLabel={<Message messageKey={'change_password'} sectionId={MessageSections.USER}/>}
                onSave={() => handleSave()}
                onCancel={() => onClose(false)}/>
        </>
    )
}

interface  PersonalInfoChangePasswordProps {
    onClose: (withChange: boolean) => void,
}
